import { useEffect, useState, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { Button } from './Button';
import { useAuth } from './auth/AuthContext';

// UX-17 : extrait de `apps/web-admin/src/components/Sidebar.tsx` (UX-09/UX-14)
// vers `packages/ui` — patron de navigation UNIQUE partagé par `web-admin` ET
// `web-parent` (sidebar fixe à gauche en desktop, tiroir hors-écran en
// mobile), au lieu d'un mécanisme distinct par app (`BottomTabBar.tsx`,
// retiré par ce même ticket). `NavigationSidebar` est PURE présentation —
// groupes/liens/badges/libellés passés en props, AUCUNE résolution de rôle ni
// appel réseau ici : chaque app garde son propre composant wrapper
// (`apps/web-admin/src/components/Sidebar.tsx`,
// `apps/web-parent/src/components/Sidebar.tsx`) qui calcule SES groupes
// (rôles/permissions/badges propres à son domaine métier) et les passe en
// props à ce composant partagé.
//
// ⚠️ **Mécanisme CONSERVÉ À L'IDENTIQUE depuis UX-14, ne JAMAIS diverger** :
// état React local (`window.innerWidth` + listener `resize`), PAS du CSS pur
// — jsdom ne calcule aucune media query, un test réel du comportement
// ouvert/fermé serait sinon structurellement impossible (voir UX-02, qui a
// documenté cette même limite pour un besoin purement visuel, sans exiger de
// preuve testée).
//
// ⚠️ **UX-55 — seuil relevé de `640px` (`sm`) à `1024px` (`lg`), breakpoint
// mal calibré confirmé RÉELLEMENT (pas supposé)** : vérifié via Playwright
// contre `web-admin` à 800px (tablette portrait, la largeur explicitement
// citée par le ticket) — la sidebar fixe restait active (256px), réduisant
// l'espace utile à ~544px, causant le débordement horizontal hors-écran
// constaté sur 4 pages réelles (Équipe/Cycles et classes/Factures impayées/
// Par classe) ET la troncature de noms sur le panneau Réinscriptions (voir
// `ReenrollmentsPanel.tsx`, même ticket). À 390px (téléphone), le mécanisme
// fonctionnait déjà correctement (aucun débordement mesuré) — seul le seuil
// tablette était en cause. `1024` (`lg`) choisi par cohérence avec le seul
// autre précédent de ce type déjà dans ce monorepo (`Students.tsx`,
// `lg:flex-row`/`lg:w-60` pour son propre sous-panneau de classes qui
// s'empile de la même façon sous ce seuil) — jamais une valeur arbitraire.
// `<aside>` ci-dessous (branche desktop) suit le MÊME seuil (`lg:`, pas
// `sm:`) — les deux DOIVENT rester en lockstep, jamais deux seuils
// divergents entre le JS qui décide de la branche et le CSS qui l'habille.
const MOBILE_BREAKPOINT_PX = 1024;

function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < MOBILE_BREAKPOINT_PX;
}

const SIDEBAR_LINK_BASE = 'ss-sidebar__link block rounded-md px-3 py-2 text-sm font-medium transition-colors';
const SIDEBAR_LINK_INACTIVE = 'text-text-tertiary hover:bg-surface-raised hover:text-text';
const SIDEBAR_LINK_ACTIVE = 'ss-sidebar__link--active bg-brand-50 text-brand-700';

function sidebarLinkClassName({ isActive }: { isActive: boolean }): string {
  return [SIDEBAR_LINK_BASE, isActive ? SIDEBAR_LINK_ACTIVE : SIDEBAR_LINK_INACTIVE, 'flex items-center justify-between'].join(
    ' ',
  );
}

function badgePill(count: number, testId: string) {
  return (
    <span className="ml-1.5 rounded-full bg-brand-600 px-1.5 py-0.5 text-xs font-semibold text-white" data-testid={testId}>
      {count}
    </span>
  );
}

export interface SidebarLink {
  to: string;
  label: string;
  badge?: number | null;
  // Optionnel — chaque app peut préserver un `data-testid` DÉJÀ établi (ex.
  // web-admin, `enrollments-pending-badge`, ENR-02) pour ne casser aucun test
  // existant lors de la migration. Repli générique sinon (dérivé de `to`).
  badgeTestId?: string;
  // UX-21 — emoji Unicode DÉCORATIF uniquement (`aria-hidden`, voir le
  // rendu ci-dessous) : le nom accessible du lien reste `label` seul, jamais
  // doublé. Optionnel — un lien sans `icon` s'affiche exactement comme avant
  // ce ticket.
  icon?: string;
}

export interface SidebarGroup {
  key: string;
  label: string;
  items: SidebarLink[];
  badge?: number | null;
  badgeTestId?: string;
  /**
   * UX-46 — quand fourni, l'en-tête du groupe (jusqu'ici un `<div>` de texte
   * statique) devient lui-même un lien cliquable (`NavLink`) vers cette
   * route, en plus de rester le conteneur visuel de `label`/`badge`.
   * Optionnel/additif (défaut absent) : AUCUN changement pour un groupe qui
   * ne le fournit pas — même discipline que `extraLinks`(UX-28)/
   * `footerLinks`(UX-43), les deux précédents mécanismes additifs de ce
   * composant. `data-testid="sidebar-group-${key}"` reste sur le `<div>`
   * englobant (INCHANGÉ), un second `data-testid` dédié
   * (`sidebar-group-${key}-header-link`) est posé sur le lien lui-même pour
   * le distinguer sans ambiguïté d'un futur item de groupe qui porterait le
   * même libellé.
   */
  headerTo?: string;
}

export interface NavigationSidebarProps {
  /** Affiché dans la barre mobile (hamburger) — ex. "Mon Ecolier". */
  brandLabel: string;
  /**
   * Lien TOUJOURS visible en tête de nav, hors groupe — ex. Accueil/"/".
   * Optionnel (SCH-65) : un appelant peut le masquer complètement pour un
   * profil dont le point d'entrée générique n'a pas de sens (ex. un compte
   * TEACHER pur, dont "Accueil enseignant" — dans `groups` — joue déjà ce
   * rôle). Défaut `undefined` : AUCUN changement pour tout appelant existant.
   */
  homeLink?: SidebarLink;
  /**
   * UX-28 — liens de PREMIER NIVEAU additionnels, hors groupe, rendus
   * IDENTIQUEMENT à `homeLink` (même style, pas de badge), juste après lui
   * et avant tout groupe. Optionnel/additif (défaut `[]`) : AUCUN changement
   * pour un appelant qui ne le fournit pas (`apps/web-admin`, inchangé).
   * Ajouté pour un domaine dont le groupe ne contenait qu'UN SEUL item
   * (ex. "Documents" → "Aperçu", web-parent) — un vrai lien de premier
   * niveau plutôt qu'un groupe à une seule entrée (en-tête + sous-lien
   * redondants). `homeLink` reste un slot séparé et unique (page d'accueil,
   * toujours en tout premier, `end` exact) — `extraLinks` ne le remplace
   * jamais, il s'ajoute juste après.
   */
  extraLinks?: SidebarLink[];
  /** Groupes de domaine, déjà filtrés par rôle par l'appelant — vide si
   *  aucun groupe ne doit être affiché (ex. utilisateur anonyme). */
  groups: SidebarGroup[];
  /**
   * UX-43 — liens de PIED de nav, rendus APRÈS `homeLink`/`extraLinks`/tous
   * les groupes, DANS le même bloc `mt-auto` que "Se déconnecter" (donc
   * toujours poussés en bas, juste au-dessus de ce bouton) — plutôt qu'un
   * lien de PREMIER NIVEAU (`extraLinks`, sous `homeLink`, UX-28), qui ne
   * peut structurellement pas se positionner en pied de nav. Optionnel/
   * additif (défaut `[]`) : AUCUN changement pour un appelant qui ne le
   * fournit pas (`apps/web-parent`, inchangé). Rendu UNIQUEMENT dans le bloc
   * déjà gardé par `isAuthenticated` (comme "Se déconnecter") — un appelant
   * n'a donc jamais besoin de gater lui-même ces liens sur l'authentification.
   *
   * ⚠️ Le ticket d'origine (UX-43) décrit cet emplacement comme situé
   * "au-dessus du bloc profil utilisateur (avatar + nom + rôle) et du
   * sélecteur de rôle" — vérifié (Étape 0) : **aucun bloc de ce type
   * n'existe dans ce composant ni ailleurs dans ce monorepo** à ce jour,
   * seul "Se déconnecter" occupe le pied de nav. `footerLinks` est donc
   * rendu directement au-dessus de CE bouton, le meilleur emplacement
   * disponible avec la structure réelle actuelle — un futur ticket qui
   * introduirait un vrai bloc profil/sélecteur de rôle devra RÉUTILISER ce
   * même `mt-auto`, jamais un second mécanisme de pied de page.
   */
  footerLinks?: SidebarLink[];
  /**
   * PAR-15 — emplacement libre en PIED de nav, rendu ENTRE `footerLinks` et
   * "Se déconnecter" (même bloc `mt-auto`) — pour un widget qui n'est PAS un
   * lien de navigation (ex. sélecteur de langue) et ne peut donc pas passer
   * par `footerLinks`. Optionnel/additif (défaut `null`) : AUCUN changement
   * pour un appelant qui ne le fournit pas (`apps/web-admin`, inchangé) —
   * même discipline que `extraLinks`/`footerLinks`/`headerTo` ci-dessus.
   *
   * ⚠️ **PAR-16 — DÉLIBÉRÉMENT NON gardé par `isAuthenticated`**, à la
   * différence de `footerLinks`/"Se déconnecter" (voir le rendu plus bas) :
   * un sélecteur de langue doit rester utilisable AVANT connexion (Login,
   * Register, écrans d'invitation, accueil public) — `web-admin`, qui ne
   * fournit jamais cette prop, n'est pas concerné par cette relaxation.
   */
  footerExtra?: ReactNode;
  /*
   * UX-84 — textes du composant lui-même, rendus traduisibles par l'appelant.
   * Même patron que `Modal` (`confirmLabel`/`cancelLabel`) : props optionnelles
   * avec le texte français d'origine en défaut, AUCUNE dépendance i18n dans
   * `packages/ui`. `web-parent` les passe via `t('sidebar.*')` ; `web-admin`
   * (sans i18next) n'en passe aucune et garde le français.
   */
  /** Bouton « Se déconnecter » (texte visible). Défaut : "Se déconnecter". */
  logoutLabel?: string;
  /** `aria-label` du `<nav>`. Défaut : "Navigation principale". */
  mainNavLabel?: string;
  /** `aria-label` du hamburger (mobile). Défaut : "Ouvrir le menu". */
  openMenuLabel?: string;
  /** `aria-label` du bouton X du tiroir (mobile). Défaut : "Fermer le menu". */
  closeMenuLabel?: string;
  /** `aria-label` du tiroir (`role="dialog"`, mobile). Défaut : "Menu de navigation". */
  drawerLabel?: string;
}

/**
 * Patron de navigation partagé (UX-17) : sidebar fixe à gauche (256px) en
 * desktop (`≥1024px`), tiroir hors-écran (bouton hamburger, overlay sombre,
 * fermeture au clic dessus ou sur un lien) sous 1024px — comportement
 * STRICTEMENT IDENTIQUE entre `web-admin` et `web-parent`, seuls les
 * groupes/liens fournis en props diffèrent. Seuil relevé de 640px à 1024px
 * par UX-55 (voir `MOBILE_BREAKPOINT_PX` ci-dessus) : la tablette portrait
 * (~768-820px) obtient désormais le tiroir elle aussi, plutôt que la sidebar
 * fixe qui lui laissait trop peu d'espace utile pour les tableaux denses.
 */
export function NavigationSidebar({
  brandLabel,
  homeLink,
  extraLinks = [],
  groups,
  footerLinks = [],
  footerExtra = null,
  logoutLabel = 'Se déconnecter',
  mainNavLabel = 'Navigation principale',
  openMenuLabel = 'Ouvrir le menu',
  closeMenuLabel = 'Fermer le menu',
  drawerLabel = 'Menu de navigation',
}: NavigationSidebarProps) {
  const { isAuthenticated, logout } = useAuth();
  const [isMobile, setIsMobile] = useState(isMobileViewport);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    function handleResize() {
      setIsMobile(isMobileViewport());
    }
    window.addEventListener('resize', handleResize);
    // Un `resize` survenu entre le rendu (où `isMobile` a été lu) et ce
    // `useEffect` (exécuté APRÈS, de façon asynchrone) n'avait aucun écouteur
    // et était perdu pour de bon : la sidebar restait bloquée dans l'ancien
    // mode. Resynchroniser une fois l'écouteur branché ferme cette fenêtre
    // (cause réelle du flake « Unable to find role=navigation » de
    // Sidebar.test.tsx, seuil 1023/1024px).
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Repasser en desktop (redimensionnement réel, ou rotation d'un appareil)
  // referme le tiroir en mémoire — sans ça, `drawerOpen` resterait `true` et
  // le tiroir se rouvrirait tout seul au prochain repassage sous 640px, sans
  // le moindre clic sur le hamburger.
  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  function closeDrawer() {
    setDrawerOpen(false);
  }

  // Contenu FACTORISÉ — rendu une seule fois, jamais dupliqué entre la
  // branche desktop (`<aside>`) et le tiroir mobile (même contenu EXACT dans
  // les deux cas). Le bouton "Se déconnecter" reste DANS ce `<nav>` (même
  // landmark accessible que les liens).
  const navContent: ReactNode = (
    <nav aria-label={mainNavLabel} className="flex flex-1 flex-col gap-1 p-4">
      {homeLink ? (
        <NavLink to={homeLink.to} end className={sidebarLinkClassName} onClick={closeDrawer}>
          <span className="flex items-center gap-2">
            {homeLink.icon ? <span aria-hidden="true">{homeLink.icon}</span> : null}
            {homeLink.label}
          </span>
        </NavLink>
      ) : null}
      {extraLinks.map((link) => (
        <NavLink key={link.to} to={link.to} className={sidebarLinkClassName} onClick={closeDrawer}>
          <span className="flex items-center gap-2">
            {link.icon ? <span aria-hidden="true">{link.icon}</span> : null}
            {link.label}
          </span>
        </NavLink>
      ))}
      {groups.map((group) => (
        <div key={group.key} className="mt-4 first:mt-0" data-testid={`sidebar-group-${group.key}`}>
          <div className="flex items-center px-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
            {group.headerTo ? (
              <NavLink
                to={group.headerTo}
                className="hover:text-text-tertiary"
                onClick={closeDrawer}
                data-testid={`sidebar-group-${group.key}-header-link`}
              >
                {group.label}
              </NavLink>
            ) : (
              group.label
            )}
            {group.badge ? badgePill(group.badge, group.badgeTestId ?? `sidebar-group-${group.key}-badge`) : null}
          </div>
          <div className="mt-1 flex flex-col gap-1">
            {group.items.map((item) => (
              <NavLink key={item.to} to={item.to} className={sidebarLinkClassName} onClick={closeDrawer}>
                <span className="flex items-center gap-2">
                  {item.icon ? <span aria-hidden="true">{item.icon}</span> : null}
                  {item.label}
                </span>
                {item.badge ? badgePill(item.badge, item.badgeTestId ?? `sidebar-link-badge-${item.to}`) : null}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
      {/* PAR-16 — `footerExtra` (ex. sélecteur de langue) n'est PLUS gardé
          par `isAuthenticated`, contrairement à `footerLinks`/"Se
          déconnecter" (qui, eux, restent STRICTEMENT gardés — ce sont des
          liens/actions de compte, sans rapport avec l'authentification).
          Sans ce dégroupage, un widget comme `<LanguageSwitcher />`
          (web-parent) resterait invisible sur TOUT écran non connecté
          (Login/Register/écrans d'invitation, l'accueil public lui-même) —
          exactement le trou signalé par ce ticket. `web-admin`, qui ne
          fournit jamais `footerExtra`, reste INAFFECTÉ (le bloc entier ne se
          rend alors que si authentifié, comme avant). */}
      {isAuthenticated || footerExtra ? (
        <div className="mt-auto flex flex-col gap-1 pt-4">
          {/* UX-43 — liens de pied de nav (ex. "Compte"), toujours juste
              au-dessus de "Se déconnecter", jamais après. */}
          {isAuthenticated
            ? footerLinks.map((link) => (
                <NavLink key={link.to} to={link.to} className={sidebarLinkClassName} onClick={closeDrawer}>
                  <span className="flex items-center gap-2">
                    {link.icon ? <span aria-hidden="true">{link.icon}</span> : null}
                    {link.label}
                  </span>
                </NavLink>
              ))
            : null}
          {footerExtra}
          {isAuthenticated ? (
            <Button variant="secondary" className="w-full" onClick={logout}>
              {logoutLabel}
            </Button>
          ) : null}
        </div>
      ) : null}
    </nav>
  );

  // Branche MOBILE — barre étroite (juste le hamburger) plus le tiroir
  // lui-même (fermé par défaut). Rendu conditionnel RÉEL (pas un simple
  // `hidden` CSS) — voir le commentaire sur `MOBILE_BREAKPOINT_PX` plus haut.
  if (isMobile) {
    return (
      <>
        {/* UX-83 — hamburger à GAUCHE, du même côté que le tiroir qu'il ouvre
            (le tiroir glisse depuis la gauche, voir plus bas). Il était à
            droite (`justify-between` + bouton après le nom), à l'opposé du
            panneau contrôlé. `pl-2` aligne l'icône sur le bouton « Fermer le
            menu » du tiroir (`px-2` + `p-2`). Le bouton X intérieur, le tiroir
            et la barre desktop ne changent pas. */}
        <div className="ss-sidebar-mobile-bar flex w-full items-center gap-2 border-b border-border bg-white py-3 pl-2 pr-4">
          <button
            type="button"
            aria-label={openMenuLabel}
            aria-haspopup="dialog"
            aria-expanded={drawerOpen}
            onClick={() => setDrawerOpen(true)}
            className="rounded-md p-2 text-text-tertiary hover:bg-surface-raised hover:text-text"
          >
            {/* Icône hamburger — SVG fait main, aucune dépendance d'icônes dans
                ce projet à ce jour (vérifié avant d'en introduire une). */}
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-6 w-6" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <span className="text-sm font-semibold text-text">{brandLabel}</span>
        </div>
        {drawerOpen ? (
          <div className="fixed inset-0 z-40 flex">
            {/* Fond cliquable — ferme le tiroir, même geste qu'un clic en dehors. */}
            <div
              className="absolute inset-0 bg-black/40"
              aria-hidden="true"
              onClick={() => setDrawerOpen(false)}
              data-testid="sidebar-drawer-backdrop"
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label={drawerLabel}
              className="ss-sidebar relative flex h-full w-64 max-w-[80vw] flex-col overflow-y-auto border-r border-border bg-white shadow-lg"
            >
              <div className="flex items-center justify-end px-2 pt-2">
                <button
                  type="button"
                  aria-label={closeMenuLabel}
                  onClick={() => setDrawerOpen(false)}
                  className="rounded-md p-2 text-text-tertiary hover:bg-surface-raised hover:text-text"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              {navContent}
            </div>
          </div>
        ) : null}
      </>
    );
  }

  // Branche DESKTOP (≥1024px, UX-55) — sidebar latérale permanente,
  // `drawerOpen` jamais lu ici. `lg:` (pas `sm:`) — DOIT rester aligné avec
  // `MOBILE_BREAKPOINT_PX` ci-dessus, jamais un second seuil divergent.
  return (
    <aside className="ss-sidebar flex w-full shrink-0 flex-col border-b border-border bg-white lg:h-screen lg:w-64 lg:overflow-y-auto lg:border-b-0 lg:border-r">
      {navContent}
    </aside>
  );
}
